import type { SpatialLevelAddError } from "@admin/domain/errors/spatial-level-add.error";
import type { SpatialLevelUpdateError } from "@admin/domain/errors/spatial-level-update.error";
import type { StationUpdateError } from "@admin/domain/errors/station-update.error";
import { webCoreTypes } from "@core/core-types.di";
import { FallbackError } from "@core/domain/errors/fallback.error";
import type { IotApiClient } from "@iot-platform/core";
import type {
    UserCreateToSpatialLevel,
    UserCreateToSpatialLevelBody,
    UserDto,
    UserResume,
} from "@iot-platform/user";
import { UserMapper } from "@iot-platform/user";
import type {
    SpatialLevelResumeAddUserBody,
    SpatialLevelResumeCreateBody,
    SpatialLevelResumeDto,
    SpatialLevelsResumeDto,
} from "@station/data/dto/spatial-level-resume.dto";
import type {
    SpatialLevelDto,
    SpatialLevelUpdateBody,
} from "@station/data/dto/spatial-level.dto";
import type {
    StationDto,
    StationUpdateBody,
} from "@station/data/dto/station.dto";
import { SpatialLevelResumeMapper } from "@station/data/mappers/spatial-level-resume.mapper";
import { SpatialLevelMapper } from "@station/data/mappers/spatial-level.mapper";
import { StationMapper } from "@station/data/mappers/station.mapper";
import type {
    SpatialLevelResume,
    SpatialLevelsResume,
} from "@station/domain/models/spatial-level-resume.model";
import type {
    SpatialLevel,
    SpatialLevelUpdate,
} from "@station/domain/models/spatial-level.model";
import type {
    Station,
    StationUpdate,
} from "@station/domain/models/station.model";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";
import { err, ok } from "neverthrow";

@injectable()
export class AdminDatasource {
    static readonly STATION_PATH = "admin/stations/:stationId";
    static readonly SPATIAL_LEVEL_PATH = "/spatial-level/:spatialLevelId";
    static readonly SPATIAL_LEVELS_RESUME_PATH =
        "/admin/stations/:stationId/spatial_levels/";
    static readonly SPATIAL_LEVELS_RESUME_USER_PATH = "/admin/users";

    constructor(
        @inject(webCoreTypes.infrastructure.IotApiClient)
        private readonly iotApiClient: IotApiClient,
        @inject(StationMapper)
        private readonly stationMapper: StationMapper,
        @inject(SpatialLevelMapper)
        private readonly spatialLevelMapper: SpatialLevelMapper,
        @inject(SpatialLevelResumeMapper)
        private readonly spatialLevelResumeMapper: SpatialLevelResumeMapper,
        @inject(UserMapper)
        private readonly userMapper: UserMapper,
    ) {}

    getSpatialLevelsResumeByStationId(
        stationId: string,
    ): ResultAsync<SpatialLevelsResume, FallbackError> {
        return this.iotApiClient.http
            .get<SpatialLevelsResumeDto>(
                AdminDatasource.SPATIAL_LEVELS_RESUME_PATH.replace(
                    ":stationId",
                    stationId,
                ),
            )
            .mapErr(() => new FallbackError())
            .map((response) =>
                this.spatialLevelResumeMapper.mapList(response.data),
            );
    }

    updateStation(
        stationId: string,
        updateStation: StationUpdate,
    ): ResultAsync<Station, StationUpdateError> {
        const stationUpdateBody: StationUpdateBody = {
            station: {
                name: updateStation.name,
                technical_name: updateStation.technicalName ?? null,
                latitude: updateStation.latitude ?? null,
                longitude: updateStation.longitude ?? null,
            },
        };

        return this.iotApiClient.http
            .patch<StationDto>(
                AdminDatasource.STATION_PATH.replace(":stationId", stationId),
                stationUpdateBody,
            )
            .mapErr(() => new FallbackError())
            .andThen((response) => this.stationMapper.map(response.data));
    }

    addSpatialLevel(
        stationId: string,
        spatialLevelId: string,
    ): ResultAsync<SpatialLevelResume, SpatialLevelAddError> {
        const spatialLevelResumeCreateBody: SpatialLevelResumeCreateBody = {
            spatial_level_id: Number(spatialLevelId),
        };

        return this.iotApiClient.http
            .post<SpatialLevelResumeDto>(
                AdminDatasource.SPATIAL_LEVELS_RESUME_PATH.replace(
                    ":stationId",
                    stationId,
                ),
                spatialLevelResumeCreateBody,
            )
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.spatialLevelResumeMapper.map(response.data),
            );
    }

    updateSpatialLevel(
        spatialLevelId: string,
        updateSpatialLevel: SpatialLevelUpdate,
    ): ResultAsync<SpatialLevel, SpatialLevelUpdateError> {
        const spatialLevelUpdateBody: SpatialLevelUpdateBody = {
            spatialLevel: {
                name: updateSpatialLevel.name,
            },
        };

        return this.iotApiClient.http
            .put<SpatialLevelDto>(
                AdminDatasource.SPATIAL_LEVEL_PATH.replace(
                    ":spatialLevelId",
                    spatialLevelId,
                ),
                spatialLevelUpdateBody,
            )
            .mapErr(() => new FallbackError())
            .andThen((response) => this.spatialLevelMapper.map(response.data));
    }

    deleteSpatialLevel(
        stationId: string,
        spatialLevelId: string,
    ): ResultAsync<true, FallbackError> {
        return this.iotApiClient.http
            .delete(
                `${AdminDatasource.SPATIAL_LEVELS_RESUME_PATH.replace(
                    ":stationId",
                    stationId,
                )}${spatialLevelId}`,
            )
            .mapErr(() => new FallbackError())
            .map(() => true);
    }

    addUsertoSpatialLevel(
        stationId: string,
        spatialLevelId: string,
        usersIds: number[],
    ): ResultAsync<SpatialLevelResume, SpatialLevelAddError> {
        const spatialLevelResumeAddUserBody: SpatialLevelResumeAddUserBody = {
            spatial_level: {
                user_ids: usersIds,
            },
        };

        return this.iotApiClient.http
            .patch<SpatialLevelResumeDto>(
                `${AdminDatasource.SPATIAL_LEVELS_RESUME_PATH.replace(
                    ":stationId",
                    stationId,
                )}${spatialLevelId}`,
                spatialLevelResumeAddUserBody,
            )
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.spatialLevelResumeMapper.map(response.data),
            );
    }

    createUserToSpatialLevel(
        user: UserCreateToSpatialLevel,
    ): ResultAsync<UserResume, FallbackError> {
        const userCreateToSpatialLevelBody: UserCreateToSpatialLevelBody = {
            user: {
                email: user.email,
                username: user.username,
                spatial_level_ids: user.spatialLevelIds,
                field_tech: user.fieldTech,
            },
        };

        return this.iotApiClient.http
            .post<UserDto>(
                AdminDatasource.SPATIAL_LEVELS_RESUME_USER_PATH,
                userCreateToSpatialLevelBody,
            )
            .mapErr(() => new FallbackError())
            .andThen((response) => {
                const createdUser = this.userMapper.mapResume(response.data);

                if (!createdUser) return err(new FallbackError());

                return ok(createdUser);
            });
    }
}
